import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, DollarSign, Calendar, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

/**
 * CustomOfferModal Component
 * Allows Giggers to create custom offers in chat
 */
const CustomOfferModal = ({
    isOpen,
    onClose,
    recipientId,
    conversationId,
    baseGig = null,
    onSuccess
}) => {
  const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        deliveryDays: 3,
        revisions: 1,
        requirements: ['']
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addRequirement = () => {
        setFormData(prev => ({
            ...prev,
            requirements: [...prev.requirements, '']
        }));
    };

    const removeRequirement = (index) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.filter((_, i) => i !== index)
        }));
    };

    const updateRequirement = (index, value) => {
        setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map((req, i) => i === index ? value : req)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            toast.error('Please enter a title');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Please enter a description');
            return;
        }
        if (!formData.price || formData.price <= 0) {
            toast.error('Please enter a valid price');
            return;
        }

        try {
            setLoading(true);

            const payload = {
                recipientId,
                conversationId,
                baseGigId: baseGig?.id || baseGig?._id,
                title: formData.title,
                description: formData.description,
                pricing: {
                    amount: parseFloat(formData.price)
                },
                deliveryDays: parseInt(formData.deliveryDays),
                revisions: parseInt(formData.revisions),
                requirements: formData.requirements.filter(r => r.trim() !== '')
            };

            const response = await api.post('/custom-offers', payload);

            toast.success('Custom offer sent successfully!');

            if (onSuccess) {
                onSuccess(response.data.data);
            }

            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create offer');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">{t('modals.create_custom_offer')}</h2>
                        <p className="text-sm text-gray-500">{t('modals.personalized_offer_desc')}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Offer Title *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="e.g., Custom Logo Design Package"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            maxLength={100}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.title.length}/100 characters
                        </p>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description *
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder={t('modals.describe_offer')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                            maxLength={1000}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.description.length}/1000 characters
                        </p>
                    </div>

                    {/* Price, Delivery, Revisions Grid */}
                    <div className="grid grid-cols-3 gap-4">
                        {/* Price */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <DollarSign className="w-4 h-4 inline mr-1" />
                                Price (USD) *
                            </label>
                            <input
                                type="number"
                                value={formData.price}
                                onChange={(e) => handleChange('price', e.target.value)}
                                placeholder="500"
                                min="1"
                                step="0.01"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Delivery Days */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Calendar className="w-4 h-4 inline mr-1" />
                                Delivery Days *
                            </label>
                            <input
                                type="number"
                                value={formData.deliveryDays}
                                onChange={(e) => handleChange('deliveryDays', e.target.value)}
                                min="1"
                                max="90"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Revisions */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <RefreshCw className="w-4 h-4 inline mr-1" />
                                Revisions *
                            </label>
                            <input
                                type="number"
                                value={formData.revisions}
                                onChange={(e) => handleChange('revisions', e.target.value)}
                                min="0"
                                max="10"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Requirements */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Requirements (Optional)
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                            What do you need from the Shipper to get started?
                        </p>

                        <div className="space-y-2">
                            {formData.requirements.map((req, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={req}
                                        onChange={(e) => updateRequirement(index, e.target.value)}
                                        placeholder="e.g., Brand name and tagline"
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {formData.requirements.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRequirement(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {formData.requirements.length < 10 && (
                            <button
                                type="button"
                                onClick={addRequirement}
                                className="mt-2 flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                            >
                                <Plus className="w-4 h-4" />
                                <span>{t('modals.add_requirement')}</span>
                            </button>
                        )}
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> This offer will expire in 24 hours. The Shipper can accept or decline your offer.
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Sending...' : 'Send Offer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CustomOfferModal;
